import { useState } from "react";
import { Reveal } from "@/components/common/Reveal";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useTheme } from "@/hooks/useTheme";

const NAV_LINKS = [
  { label: "Story", hash: "#story", dotColor: "bg-sky-500" },
  { label: "Principles", hash: "#principles", dotColor: "bg-purple-500" },
  { label: "Team", hash: "#team", dotColor: "bg-emerald-500" },
  { label: "Milestones", hash: "#milestones", dotColor: "bg-amber-500" },
  { label: "Careers", hash: "#careers", dotColor: "bg-teal-500" },
];

interface Node {
  x: number;
  photo: string | null;
  initials: string;
  name?: string;
  role?: string;
  small: boolean;
  isLogo?: boolean;
}

const NODES: Node[] = [
  { x: -420, photo: null, initials: "RW", small: true },
  { x: -300, photo: null, initials: "NL", small: true },
  { x: -150, photo: null, initials: "NL", small: true },
  { x: -10, photo: "/team-yinyin.jpg", initials: "YW", name: "Yinyin Wang", role: "QA", small: true },
  { x: 120, photo: null, initials: "ZL", name: "Zhongxin Liu", role: "Product Lead", small: true },
  { x: 240, photo: "/team-george.png", initials: "GY", name: "George Yang", role: "Strategic Advisor", small: false },
  { x: 460, photo: "/team-ben.jpg", initials: "BZ", name: "Ben Zhang", role: "Co-Founder, Technical", small: false },
  { x: 530, photo: null, initials: "", small: false, isLogo: true },
  { x: 620, photo: "/team-hugh.jpg", initials: "HC", name: "Hugh Carnegie", role: "Co-Founder, Operations", small: false },
  { x: 730, photo: "/team-cher.png", initials: "CT", name: "Cher Truman", role: "Marketing & Growth", small: false },
  { x: 810, photo: "/team-milo.jpg", initials: "MR", name: "Milo Rowse", role: "Customer Success", small: false },
  { x: 920, photo: "/team-xun.jpg", initials: "XZ", name: "Xun Zhang", role: "Tech Lead", small: true },
  { x: 1060, photo: null, initials: "ZL", small: true },
  { x: 1180, photo: null, initials: "LJ", name: "Lei Jiang", role: "Frontend Lead", small: true },
  { x: 1310, photo: "/team-xiang.png", initials: "XW", name: "Xiang Wang", role: "AI / ML", small: true },
  { x: 1450, photo: null, initials: "YW", small: true },
];

function waveY(x: number): number {
  return 150 - 45 * Math.cos(((x - 530) / 200) * Math.PI);
}

function wavePath(): string {
  const pts: string[] = [];
  for (let x = -500; x <= 1560; x += 2) {
    pts.push(`${x === -500 ? "M" : "L"}${x},${waveY(x).toFixed(1)}`);
  }
  return pts.join(" ");
}

const FORMULAS = [
  { text: "cos(θ)", x: -420, y: 90, size: 7, o: 0.2 },
  { text: "Δx", x: -380, y: 210, size: 7, o: 0.15 },
  { text: "sin²θ + cos²θ = 1", x: -300, y: 140, size: 5, o: 0.12 },
  { text: "λ = c/f", x: -240, y: 200, size: 5.5, o: 0.14 },
  { text: "A(t) = A₀e^(-γt)", x: -150, y: 95, size: 5, o: 0.12 },
  { text: "Σ", x: -80, y: 195, size: 14, o: 0.1 },
  { text: "∮", x: -50, y: 100, size: 10, o: 0.1 },
  { text: "d/dx cos(x) = -sin(x)", x: -20, y: 185, size: 4.5, o: 0.12 },
  { text: "f(x) = A·cos(ωx + φ)", x: 70, y: 90, size: 5.5, o: 0.16 },
  { text: "∫₀^π cos(x)dx = 0", x: 140, y: 210, size: 4.5, o: 0.12 },
  { text: "α", x: 210, y: 100, size: 8, o: 0.12 },
  { text: "ℝ → ℝ", x: 300, y: 85, size: 5, o: 0.1 },
  { text: "e^(iθ) = cos(θ) + i·sin(θ)", x: 320, y: 210, size: 5, o: 0.12 },
  { text: "T = 2π/ω", x: 580, y: 85, size: 5.5, o: 0.18 },
  { text: "x₀", x: 460, y: 210, size: 6, o: 0.14 },
  { text: "lim x→∞", x: 650, y: 210, size: 5, o: 0.12 },
  { text: "y = cos(x)", x: 420, y: 88, size: 5.5, o: 0.18 },
  { text: "∫₋∞^∞", x: 550, y: 215, size: 6, o: 0.12 },
  { text: "n→∞", x: 700, y: 90, size: 5, o: 0.12 },
  { text: "Δt", x: 490, y: 85, size: 6, o: 0.14 },
  { text: "∫cos(x)dx = sin(x) + C", x: 730, y: 200, size: 5, o: 0.14 },
  { text: "∂f/∂x", x: 840, y: 100, size: 7, o: 0.15 },
  { text: "cos(2θ) = 2cos²θ - 1", x: 870, y: 205, size: 4.5, o: 0.12 },
  { text: "∞", x: 950, y: 88, size: 10, o: 0.1 },
  { text: "ω = 2πf", x: 1020, y: 200, size: 5.5, o: 0.18 },
  { text: "cos(x) = Re(e^(ix))", x: 1060, y: 100, size: 5, o: 0.12 },
  { text: "∇²", x: 1170, y: 190, size: 10, o: 0.1 },
  { text: "φ₀", x: 1140, y: 95, size: 8, o: 0.12 },
  { text: "F(ω) = ∫f(t)e^(-iωt)dt", x: 1240, y: 130, size: 4.5, o: 0.1 },
  { text: "π", x: 1310, y: 200, size: 9, o: 0.1 },
  { text: "cos(α-β)", x: 1380, y: 105, size: 5, o: 0.12 },
  { text: "θ", x: 1430, y: 190, size: 8, o: 0.1 },
  { text: "dx/dt", x: 1480, y: 140, size: 5, o: 0.12 },
];

export function AboutHero() {
  const prefersReducedMotion = useReducedMotion();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const path = wavePath();
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);

  const handleNodeClick = (node: Node) => {
    if (node.isLogo || !node.name) return;
    const teamSection = document.getElementById("team");
    if (teamSection) {
      const topnavHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--topnav-height") || "64", 10);
      const top = teamSection.getBoundingClientRect().top + window.scrollY - topnavHeight - 16;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <section className="relative z-10 flex flex-col items-center pt-[var(--topnav-height)] pb-0 overflow-hidden">
      <div className="max-w-6xl mx-auto w-full text-center pt-16 mb-10 px-8">
        <Reveal>
          <h1 className="text-3xl md:text-5xl font-light tracking-tight mb-4 font-[family-name:var(--font-display)]">
            About COSX
          </h1>
        </Reveal>
        <Reveal delay={100}>
          <p className="text-lg text-[var(--color-fg-secondary)] max-w-2xl mx-auto font-light mb-10">
            We came from regulated industries. We know what compliance actually costs. We're building the AI platform we wished existed.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <nav className="flex flex-wrap justify-center gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.hash}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] border-b border-[var(--color-border)] hover:border-[var(--color-fg-secondary)] transition-colors"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${link.dotColor}`} />
                <span>{link.label}</span>
              </a>
            ))}
          </nav>
        </Reveal>
      </div>

      <Reveal delay={300}>
        <div className="w-screen relative overflow-hidden hero-strip-mask" style={{ marginTop: "-12px" }}>
          <div className="relative h-[180px] md:h-[240px] overflow-hidden">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: "radial-gradient(circle, var(--color-fg) 0.5px, transparent 0.5px)",
                backgroundSize: "20px 20px",
                opacity: 0.08,
              }}
            />
            <svg
              viewBox="-500 0 2060 300"
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="xMidYMid slice"
            >
              <defs>
                {NODES.map((node, i) => {
                  if (!node.photo) return null;
                  const r = node.small ? 10 : 18;
                  const y = node.isLogo ? 150 : waveY(node.x);
                  return (
                    <clipPath key={`clip${i}`} id={`about-photo-${i}`}>
                      <circle cx={node.x} cy={y} r={r} />
                    </clipPath>
                  );
                })}
              </defs>

              {/* Axes */}
              <line x1={-480} y1={150} x2={1540} y2={150} stroke="var(--color-fg)" strokeWidth="0.6" opacity={0.2} />
              <line x1={530} y1={20} x2={530} y2={280} stroke="var(--color-fg)" strokeWidth="0.6" opacity={0.2} />
              <path d="M1535,147 L1545,150 L1535,153" fill="none" stroke="var(--color-fg)" strokeWidth="0.4" opacity={0.15} />
              <path d="M527,25 L530,15 L533,25" fill="none" stroke="var(--color-fg)" strokeWidth="0.4" opacity={0.15} />
              {[-400, -300, -200, -100, 0, 100, 200, 300, 400, 600, 700, 800, 900, 1000, 1100, 1200, 1300, 1400, 1500].map((tx) => (
                <line key={`tx${tx}`} x1={tx} y1={147} x2={tx} y2={153} stroke="var(--color-fg)" strokeWidth="0.4" opacity={0.12} />
              ))}
              {[60, 100, 200, 240].map((ty) => (
                <line key={`ty${ty}`} x1={527} y1={ty} x2={533} y2={ty} stroke="var(--color-fg)" strokeWidth="0.4" opacity={0.12} />
              ))}
              <text x={1335} y={142} fill="var(--color-fg)" opacity={0.15} fontSize="5" fontFamily="var(--font-mono)" fontStyle="italic">x</text>
              <text x={536} y={18} fill="var(--color-fg)" opacity={0.15} fontSize="5" fontFamily="var(--font-mono)" fontStyle="italic">y</text>
              <text x={540} y={160} fill="var(--color-fg)" opacity={0.12} fontSize="4" fontFamily="var(--font-mono)" fontStyle="italic">0</text>

              {/* Formulas */}
              {FORMULAS.map((f, i) => (
                <text
                  key={`f${i}`}
                  x={f.x} y={f.y}
                  fill="var(--color-fg)" opacity={f.o}
                  fontSize={f.size}
                  fontFamily="var(--font-mono)"
                  fontStyle="italic"
                  style={prefersReducedMotion ? {} : {
                    animation: `formulaDrift ${12 + i * 1.3}s ease-in-out infinite`,
                    animationDelay: `${i * 0.8}s`,
                  }}
                >
                  {f.text}
                </text>
              ))}

              {/* Cosine wave (responds to mouse) */}
              <path d={path} fill="none" stroke="var(--color-fg)" strokeWidth="1.2" opacity={0.15} />
              <path d={path} fill="none" stroke="var(--color-fg)" strokeWidth="0.4" opacity={0.06} transform="translate(0, 5)" />

              {/* Traveling pulses */}
              {!prefersReducedMotion && (
                <>
                  <circle r="2.5" fill="var(--color-fg)" opacity={0}>
                    <animateMotion dur="12s" repeatCount="indefinite" path={path} />
                    <animate attributeName="opacity" values="0;0.2;0.2;0" dur="12s" repeatCount="indefinite" />
                  </circle>
                  <circle r="1.8" fill="var(--color-fg)" opacity={0}>
                    <animateMotion dur="12s" repeatCount="indefinite" begin="6s" path={path} />
                    <animate attributeName="opacity" values="0;0.15;0.15;0" dur="12s" repeatCount="indefinite" begin="6s" />
                  </circle>
                </>
              )}

              {/* Team nodes */}
              {NODES.map((node, i) => {
                const y = node.isLogo ? 150 : waveY(node.x);
                const r = node.isLogo ? 26 : node.small ? 10 : 18;
                const isHovered = hoveredNode === i;
                const hasInfo = !node.isLogo && node.name;

                return (
                  <g
                    key={i}
                    style={{
                      cursor: hasInfo ? "pointer" : undefined,
                      ...(prefersReducedMotion ? {} : {
                        animation: `nodeFloat ${7 + i * 0.4}s ease-in-out infinite`,
                        animationDelay: `${i * 0.5}s`,
                        transformOrigin: `${node.x}px ${y}px`,
                      }),
                    }}
                    onMouseEnter={() => hasInfo && setHoveredNode(i)}
                    onMouseLeave={() => setHoveredNode(null)}
                    onClick={() => handleNodeClick(node)}
                  >
                    {/* Background */}
                    <circle cx={node.x} cy={y} r={r} fill="var(--color-bg)" opacity={0.9} />
                    <circle
                      cx={node.x} cy={y} r={r}
                      fill="none" stroke="var(--color-fg)"
                      strokeWidth={isHovered ? "0.8" : "0.4"}
                      opacity={isHovered ? 0.3 : 0.12}
                      style={{ transition: "all 0.3s ease" }}
                    />

                    {node.isLogo ? (
                      <image
                        href={isDark ? "/logo-dark.svg" : "/cosx-logo.svg"}
                        x={node.x - 20}
                        y={y - 7}
                        width="40"
                        height="14"
                        opacity={0.6}
                      />
                    ) : node.photo ? (
                      <>
                        <image
                          href={node.photo}
                          x={node.x - r}
                          y={y - r}
                          width={r * 2}
                          height={r * 2}
                          clipPath={`url(#about-photo-${i})`}
                          preserveAspectRatio="xMidYMid slice"
                          opacity={isHovered ? 0.9 : 0.7}
                          style={{ transition: "opacity 0.3s ease" }}
                        />
                      </>
                    ) : (
                      <>
                        <circle cx={node.x} cy={y} r={r} fill="var(--color-fg)" opacity={isHovered ? 0.08 : 0.04} style={{ transition: "opacity 0.3s ease" }} />
                        <text
                          x={node.x} y={y}
                          fill="var(--color-fg)" opacity={isHovered ? 0.4 : 0.2}
                          fontSize={5} fontFamily="var(--font-mono)" fontWeight="500"
                          textAnchor="middle" dominantBaseline="central"
                          style={{ transition: "opacity 0.3s ease" }}
                        >
                          {node.initials}
                        </text>
                      </>
                    )}

                    {/* Tooltip on hover */}
                    {isHovered && node.name && (
                      <g>
                        <rect
                          x={node.x - 40}
                          y={y - r - 22}
                          width="80"
                          height="18"
                          rx="3"
                          fill="var(--color-fg)"
                          opacity={0.85}
                        />
                        <text
                          x={node.x}
                          y={y - r - 16}
                          fill="var(--color-bg)"
                          fontSize="4"
                          fontFamily="var(--font-mono)"
                          fontWeight="600"
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {node.name}
                        </text>
                        <text
                          x={node.x}
                          y={y - r - 10}
                          fill="var(--color-bg)"
                          fontSize="3"
                          fontFamily="var(--font-mono)"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          opacity={0.7}
                        >
                          {node.role}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      </Reveal>

      <style>{`
        @keyframes nodeFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2.5px); }
        }
        @keyframes formulaDrift {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(1.5px, -1px); }
          66% { transform: translate(-1px, 1.5px); }
        }
      `}</style>
    </section>
  );
}
